import React, { useState, useEffect } from 'react';
import getApiBase from '../../components/getApiBase';

const ConfidenceScoreDetails = ({ shopId, onClose, onScoreUpdated }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchConfidenceDetails();
  }, [shopId]);

  const fetchConfidenceDetails = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${getApiBase()}/api/confidence/calculate/${shopId}`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (response.ok) {
        const result = await response.json();
        setData(result);
        // Notify parent component of score update
        if (onScoreUpdated) {
          onScoreUpdated(shopId, result.score);
        }
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to fetch confidence details');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 80) return '#10b981';
    if (score >= 60) return '#f59e0b';
    if (score >= 40) return '#ef4444';
    return '#6b7280';
  };

  const getProgressWidth = (points, maxPoints) => {
    return maxPoints > 0 ? (points / maxPoints) * 100 : 0;
  };

  if (loading) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000
      }}>
        <div style={{
          backgroundColor: 'white',
          padding: '20px',
          borderRadius: '8px',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
        }}>
          Loading confidence details...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000
      }}>
        <div style={{
          backgroundColor: 'white',
          padding: '20px',
          borderRadius: '8px',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
          maxWidth: '400px'
        }}>
          <h3 style={{ color: '#dc2626', marginTop: 0 }}>Error</h3>
          <p>{error}</p>
          <button
            onClick={onClose}
            style={{
              backgroundColor: '#6b7280',
              color: 'white',
              border: 'none',
              padding: '8px 16px',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px',
      overflowY: 'auto'
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)',
        maxWidth: '600px',
        width: '100%',
        maxHeight: '90vh',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* Header */}
        <div style={{
          padding: '20px',
          borderBottom: '1px solid #e5e7eb',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <h2 style={{ margin: 0, color: '#1f2937' }}>Confidence Score Details</h2>
          <button
            onClick={onClose}
            style={{
              backgroundColor: 'transparent',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              color: '#6b7280',
              padding: '4px'
            }}
          >
            ×
          </button>
        </div>

        {/* Score Overview */}
        <div style={{
          padding: '20px',
          backgroundColor: '#f9fafb',
          textAlign: 'center'
        }}>
          <div style={{
            fontSize: '48px',
            fontWeight: 'bold',
            color: getScoreColor(data.score),
            marginBottom: '8px'
          }}>
            {data.score}%
          </div>
          <div style={{
            fontSize: '20px',
            color: '#6b7280',
            marginBottom: '16px'
          }}>
            {data.level} Confidence
          </div>
          <div style={{
            width: '200px',
            height: '8px',
            backgroundColor: '#e5e7eb',
            borderRadius: '4px',
            margin: '0 auto',
            overflow: 'hidden'
          }}>
            <div style={{
              width: `${data.score}%`,
              height: '100%',
              backgroundColor: getScoreColor(data.score),
              transition: 'width 0.3s ease'
            }} />
          </div>
        </div>

        {/* Breakdown */}
        <div style={{
          padding: '20px',
          overflowY: 'auto',
          flex: 1
        }}>
          <h3 style={{ marginTop: 0, marginBottom: '16px', color: '#374151' }}>
            Score Breakdown
          </h3>
          
          {data.breakdown && data.breakdown.map((category, index) => (
            <div key={index} style={{
              marginBottom: '20px',
              padding: '16px',
              backgroundColor: '#f9fafb',
              borderRadius: '8px'
            }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '8px'
              }}>
                <h4 style={{ margin: 0, color: '#1f2937' }}>{category.category}</h4>
                <span style={{ 
                  fontSize: '14px', 
                  fontWeight: '600',
                  color: category.points === category.maxPoints ? '#10b981' : '#6b7280'
                }}>
                  {category.points} / {category.maxPoints} points
                </span>
              </div>
              
              <div style={{
                width: '100%',
                height: '6px',
                backgroundColor: '#e5e7eb',
                borderRadius: '3px',
                marginBottom: '12px',
                overflow: 'hidden'
              }}>
                <div style={{
                  width: `${getProgressWidth(category.points, category.maxPoints)}%`,
                  height: '100%',
                  backgroundColor: category.points === category.maxPoints ? '#10b981' : 
                    category.points > 0 ? '#f59e0b' : '#ef4444',
                  transition: 'width 0.3s ease'
                }} />
              </div>
              
              <ul style={{
                margin: 0,
                paddingLeft: '20px',
                fontSize: '14px',
                color: '#6b7280'
              }}>
                {category.factors.map((factor, i) => (
                  <li key={i} style={{ marginBottom: '4px' }}>
                    {factor}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Recommendations */}
        {data.recommendations && data.recommendations.length > 0 && (
          <div style={{
            padding: '20px',
            borderTop: '1px solid #e5e7eb',
            backgroundColor: '#fef3c7'
          }}>
            <h3 style={{ marginTop: 0, marginBottom: '12px', color: '#92400e' }}>
              Recommendations to Improve Score
            </h3>
            <ul style={{
              margin: 0,
              paddingLeft: '20px',
              fontSize: '14px',
              color: '#92400e'
            }}>
              {data.recommendations.map((rec, index) => (
                <li key={index} style={{ marginBottom: '6px' }}>
                  {rec}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Actions */}
        <div style={{
          padding: '20px',
          borderTop: '1px solid #e5e7eb',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '12px'
        }}>
          <button
            onClick={fetchConfidenceDetails}
            style={{
              backgroundColor: '#3b82f6',
              color: 'white',
              border: 'none',
              padding: '8px 16px',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >
            Recalculate
          </button>
          <button
            onClick={onClose}
            style={{
              backgroundColor: '#6b7280',
              color: 'white',
              border: 'none',
              padding: '8px 16px',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfidenceScoreDetails;